import { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

import {
    insertData,
    selectData,
    isSupabaseConfigured,
} from '../lib/supabase';

const WEEKLY_AVAILABILITY = {
    0: [],
    1: [
        { start: '08:00', end: '14:00' },
        { start: '16:00', end: '19:00' },
    ],
    2: [
        { start: '08:00', end: '14:00' },
        { start: '16:00', end: '19:00' },
    ],
    3: [
        { start: '08:00', end: '14:00' },
        { start: '16:00', end: '19:00' },
    ],
    4: [
        { start: '08:00', end: '14:00' },
        { start: '16:00', end: '19:00' },
    ],
    5: [
        { start: '08:00', end: '14:00' },
        { start: '16:00', end: '19:00' },
    ],
    6: [
        { start: '08:00', end: '12:00' },
        { start: '14:00', end: '17:00' },
    ],
};

const SLOT_INTERVAL = 30;

function timeToMinutes(time) {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
}

function minutesToTime(totalMinutes) {
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
    const minutes = String(totalMinutes % 60).padStart(2, '0');
    return `${hours}:${minutes}`;
}

function getAvailabilityForDate(date) {
    if (!date) return [];

    const selectedDate = new Date(`${date}T00:00:00`);
    const dayOfWeek = selectedDate.getDay();

    return WEEKLY_AVAILABILITY[dayOfWeek] || [];
}

function generateBaseTimeSlots(durationMinutes, date) {
    if (!date) return [];

    const availability = getAvailabilityForDate(date);
    if (!availability.length) return [];

    const slots = [];

    availability.forEach(({ start, end }) => {
        const startMinutes = timeToMinutes(start);
        const endMinutes = timeToMinutes(end);

        for (
            let current = startMinutes;
            current + durationMinutes <= endMinutes;
            current += SLOT_INTERVAL
        ) {
            slots.push(minutesToTime(current));
        }
    });

    const today = new Date();
    const selected = new Date(`${date}T00:00:00`);
    const isToday =
        selected.getFullYear() === today.getFullYear() &&
        selected.getMonth() === today.getMonth() &&
        selected.getDate() === today.getDate();

    if (!isToday) return slots;

    const currentMinutes = today.getHours() * 60 + today.getMinutes();

    return slots.filter((slot) => timeToMinutes(slot) > currentMinutes + 30);
}

function buildDateTime(date, time) {
    return new Date(`${date}T${time}:00`);
}

function rangesOverlap(startA, endA, startB, endB) {
    return startA < endB && endA > startB;
}

export const BookingModal = () => {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
    const [existingAppointments, setExistingAppointments] = useState([]);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        date: '',
        time: '',
        duration: '60',
        subject: 'Appel découverte',
        message: '',
    });

    const durationMinutes = Number(formData.duration);

    const minDate = useMemo(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            const next = { ...prev, [name]: value };

            if (name === 'date') {
                next.time = '';
            }

            return next;
        });
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => {
            const next = { ...prev, [name]: value };

            if (name === 'duration') {
                next.time = '';
            }

            return next;
        });
    };

    const resetForm = () => {
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: '',
            date: '',
            time: '',
            duration: '60',
            subject: 'Appel découverte',
            message: '',
        });
        setExistingAppointments([]);
    };

    useEffect(() => {
        const fetchAppointmentsForDate = async () => {
            if (!formData.date) {
                setExistingAppointments([]);
                return;
            }

            if (!isSupabaseConfigured) {
                setExistingAppointments([]);
                return;
            }

            setIsLoadingAppointments(true);

            try {
                const startOfDay = new Date(`${formData.date}T00:00:00`);
                const endOfDay = new Date(`${formData.date}T23:59:59`);

                const { data, error } = await selectData('appointments', {
                    columns: 'id, starts_at, ends_at, status',
                    inFilters: {
                        status: ['pending', 'confirmed'],
                    },
                    gteFilters: {
                        starts_at: startOfDay.toISOString(),
                    },
                    lteFilters: {
                        starts_at: endOfDay.toISOString(),
                    },
                    order: {
                        column: 'starts_at',
                        ascending: true,
                    },
                });

                if (error) {
                    throw error;
                }

                setExistingAppointments(data || []);
            } catch (error) {
                console.error('Erreur chargement rendez-vous :', error);
                setExistingAppointments([]);
            } finally {
                setIsLoadingAppointments(false);
            }
        };

        fetchAppointmentsForDate();
    }, [formData.date]);

    const timeSlots = useMemo(() => {
        const baseSlots = generateBaseTimeSlots(durationMinutes, formData.date);

        if (!formData.date) return baseSlots;
        if (!existingAppointments.length) return baseSlots;

        return baseSlots.filter((slot) => {
            const slotStart = buildDateTime(formData.date, slot);
            const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60 * 1000);

            const overlapsExisting = existingAppointments.some((appointment) => {
                const existingStart = new Date(appointment.starts_at);
                const existingEnd = new Date(appointment.ends_at);

                return rangesOverlap(slotStart, slotEnd, existingStart, existingEnd);
            });

            return !overlapsExisting;
        });
    }, [durationMinutes, formData.date, existingAppointments]);

    const selectedSlotAvailable = useMemo(() => {
        if (!formData.date || !formData.time) return false;
        return timeSlots.includes(formData.time);
    }, [formData.date, formData.time, timeSlots]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.firstName || !formData.email || !formData.date || !formData.time) {
            toast.error('Veuillez remplir les champs obligatoires.');
            return;
        }

        const startsAt = buildDateTime(formData.date, formData.time);
        const endsAt = new Date(startsAt.getTime() + durationMinutes * 60 * 1000);

        setIsLoading(true);

        try {
            const payload = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                company: formData.company,
                subject: formData.subject,
                message: formData.message,
                appointment_date: formData.date,
                starts_at: startsAt.toISOString(),
                ends_at: endsAt.toISOString(),
                duration_minutes: durationMinutes,
                status: 'pending',
            };

            const { error, demo } = await insertData('appointments', payload);

            if (error) {
                throw error;
            }

            if (!demo && isSupabaseConfigured) {
                try {
                    await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-booking-notification`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                        },
                        body: JSON.stringify({
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            email: formData.email,
                            phone: formData.phone,
                            company: formData.company,
                            date: formData.date,
                            time: formData.time,
                            duration: durationMinutes,
                            subject: formData.subject,
                            message: formData.message,
                        }),
                    });
                } catch (emailError) {
                    console.error('Erreur notification email admin :', emailError);
                }
            }

            if (demo || !isSupabaseConfigured) {
                toast.success('Réservation enregistrée ! (mode démo)');
            } else {
                toast.success('Votre créneau a bien été réservé.');
            }

            resetForm();
            setOpen(false);
        } catch (error) {
            const message =
                error?.message?.includes('appointments_no_overlap') ||
                error?.message?.toLowerCase?.().includes('conflict')
                    ? 'Ce créneau est déjà réservé. Merci d’en choisir un autre.'
                    : 'Une erreur est survenue lors de la réservation.';

            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(value) => {
                setOpen(value);
                if (!value) {
                    resetForm();
                }
            }}
        >
            <DialogTrigger asChild>
                <Button variant="outline" className="w-full weblax-btn-secondary">
                    Réserver un créneau
                </Button>
            </DialogTrigger>

            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Réserver un rendez-vous</DialogTitle>
                    <DialogDescription>
                        Choisissez une date, une heure et renseignez vos informations.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">Prénom *</Label>
                            <Input
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                placeholder="Votre prénom"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="lastName">Nom</Label>
                            <Input
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                placeholder="Votre nom"
                            />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="votre@email.com"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="phone">Téléphone</Label>
                            <Input
                                id="phone"
                                name="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+33 6 12 34 56 78"
                            />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="company">Entreprise</Label>
                            <Input
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder="Nom de votre entreprise"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Durée *</Label>
                            <Select
                                value={formData.duration}
                                onValueChange={(value) => handleSelectChange('duration', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Choisir une durée" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="60">1h</SelectItem>
                                    <SelectItem value="90">1h30</SelectItem>
                                    <SelectItem value="120">2h</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <Card className="border-primary/20 bg-primary/5">
                        <CardContent className="p-4 grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="date" className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Date *
                                </Label>
                                <Input
                                    id="date"
                                    name="date"
                                    type="date"
                                    min={minDate}
                                    value={formData.date}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    Heure *
                                </Label>
                                <Select
                                    value={formData.time}
                                    onValueChange={(value) => handleSelectChange('time', value)}
                                    disabled={!formData.date || isLoadingAppointments}
                                >
                                    <SelectTrigger>
                                        <SelectValue
                                            placeholder={
                                                isLoadingAppointments
                                                    ? 'Chargement des créneaux...'
                                                    : 'Choisir un créneau'
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {timeSlots.length > 0 ? (
                                            timeSlots.map((slot) => (
                                                <SelectItem key={slot} value={slot}>
                                                    {slot}
                                                </SelectItem>
                                            ))
                                        ) : (
                                            <SelectItem value="no-slot-disabled" disabled>
                                                {isLoadingAppointments
                                                    ? 'Chargement...'
                                                    : 'Aucun créneau disponible'}
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>

                                {formData.time && selectedSlotAvailable && (
                                    <p className="text-sm text-green-600">
                                        Le créneau choisi est disponible.
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <div className="space-y-2">
                        <Label htmlFor="message">Informations complémentaires</Label>
                        <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Décrivez brièvement votre besoin"
                            className="min-h-[120px]"
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 justify-end">
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                            Annuler
                        </Button>
                        <Button type="submit" disabled={isLoading || isLoadingAppointments}>
                            {isLoading ? 'Réservation en cours...' : 'Confirmer le rendez-vous'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default BookingModal;
